import type {
  EmailBusinessUsageReport,
  EmailBusinessUsageResponse,
  EmailMerchantUsageReport,
  EmailMerchantUsageResponse,
  EmailNotificationPageResponse,
  EmailNotificationPagingResult,
  EmailSendLogDetail,
  EmailSendLogDetailResponse,
  EmailSendLogItem,
  EmailSendLogQuery,
  EmailSendLogResponseItem,
  EmailTemplateDetail,
  EmailTemplateDetailResponse,
  EmailTemplateItem,
  EmailTemplateListResponseItem,
  EmailTemplatePreviewResult,
  EmailTemplateQuery,
  EmailUsageQuery,
  PreviewEmailTemplateParams,
  PublishEmailTemplateParams,
  TestSendEmailParams,
  TestSendEmailResult,
} from './types'

import type { PagingRequest } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { EXPORT_TASK_BIZ_CODE, exportTaskApi } from '#/api/modules/common'
import { requestClient } from '#/api/request'
import { DateRanges } from '#/utils'

type EmailTemplatePageRequest = PagingRequest<{
  condition?: EmailTemplateQuery
}>

type EmailSendLogPageRequest = PagingRequest<{
  condition?: EmailSendLogQuery
}>

type EmailMerchantUsagePageRequest = PagingRequest<{
  condition?: EmailUsageQuery
}>

export const emailNotificationApi = defineApi('operation', ({ prefix }) => {
  function getSendLogCondition(query?: EmailSendLogQuery) {
    return {
      customerId: query?.merchantId ? Number(query.merchantId) : undefined,
      customerName: query?.customerName,
      dateRange: DateRanges.dates(query?.dateRange),
      isTest: 0,
      sceneCodes: query?.scenes,
      sendStatus: query?.status,
    }
  }

  function getUsageCondition(query?: EmailUsageQuery) {
    return {
      dateRange: DateRanges.dates(query?.dateRange),
      sceneCodes: query?.scenes,
    }
  }

  function getMerchantUsageCondition(query?: EmailUsageQuery) {
    return {
      ...getUsageCondition(query),
      customerId: query?.merchantId ? Number(query.merchantId) : undefined,
      customerName: query?.customerName,
    }
  }

  /**
   * 查询邮件模板列表。
   * POST /gobestads/operation/adsEmailTemplate/queryPage
   */
  function queryTemplates(
    params: EmailTemplatePageRequest,
  ): Promise<EmailNotificationPagingResult<EmailTemplateItem>> {
    return requestClient
      .post<EmailNotificationPageResponse<EmailTemplateListResponseItem>>(
        prefix('adsEmailTemplate/queryPage'),
        {
          condition: {
            publishStatus: params.condition?.status,
            sceneCode: params.condition?.scene,
            templateCode: params.condition?.templateCode,
          },
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
        },
      )
      .then((res) => ({
        items: (res.list ?? []).map((item) => ({
          code: item.templateCode,
          scene: item.sceneCode,
          source: item.templateSource,
          status: item.publishStatus,
          subjectSample: item.subjectSample,
          templateName: item.templateName,
          updatedBy: item.updatedBy,
          updatedTime: item.updatedTime,
        })),
        total: res.total,
      }))
  }

  /**
   * 获取邮件模板详情。
   * POST /gobestads/operation/adsEmailTemplate/getDetail
   */
  function getTemplate(templateCode: string): Promise<EmailTemplateDetail> {
    return requestClient
      .post<EmailTemplateDetailResponse>(prefix('adsEmailTemplate/getDetail'), {
        templateCode,
      })
      .then((res) => ({
        code: res.templateCode,
        defaultLocale: res.defaultLocale,
        editableLocale: res.editableLocale,
        locales: res.contents,
        scene: res.sceneCode,
        secondaryCtaAllowed: res.secondaryCtaAllowed,
        source: res.templateSource,
        status: res.publishStatus,
        table: res.table,
        templateName: res.templateName,
        variables: res.variables,
      }))
  }

  /**
   * 预览邮件模板。
   * POST /gobestads/operation/adsEmailTemplate/preview
   */
  function previewTemplate(params: PreviewEmailTemplateParams) {
    return requestClient.post<EmailTemplatePreviewResult>(
      prefix('adsEmailTemplate/preview'),
      params,
    )
  }

  /**
   * 测试发送邮件模板。
   * POST /gobestads/operation/adsEmailTemplate/testSend
   */
  function testSendTemplate(params: TestSendEmailParams) {
    return requestClient.post<TestSendEmailResult>(
      prefix('adsEmailTemplate/testSend'),
      {
        content: params.content,
        locale: params.locale,
        templateCode: params.templateCode,
        testEmail: params.recipientEmail,
      },
    )
  }

  /**
   * 发布邮件模板。
   * POST /gobestads/operation/adsEmailTemplate/publish
   */
  function publishTemplate(params: PublishEmailTemplateParams) {
    return requestClient.post<null>(prefix('adsEmailTemplate/publish'), {
      contents: params.locales,
      templateCode: params.templateCode,
      templateName: params.templateName,
    })
  }

  /**
   * 查询邮件发送日志。
   * POST /gobestads/operation/adsEmailSendLog/queryPage
   */
  function querySendLogs(
    params: EmailSendLogPageRequest,
  ): Promise<EmailNotificationPagingResult<EmailSendLogItem>> {
    return requestClient
      .post<EmailNotificationPageResponse<EmailSendLogResponseItem>>(
        prefix('adsEmailSendLog/queryPage'),
        {
          condition: getSendLogCondition(params.condition),
          pageIndex: params.pageIndex,
          pageSize: params.pageSize,
        },
      )
      .then((res) => ({
        items: (res.list ?? []).map((item) => ({
          batchNo: item.batchNo,
          customerName: item.customerName,
          id: item.id,
          isTest: item.isTest,
          merchantId: item.customerId,
          messageId: item.messageId,
          reason: item.reason,
          recipients: item.recipientEmailMasked,
          scene: item.sceneCode,
          sendTime: item.createdTime,
          sentAt: item.sentAt,
          status: item.sendStatus,
          templateCode: item.templateCode,
          triggerSource: item.operator,
        })),
        total: res.total,
      }))
  }

  /**
   * 获取邮件发送日志详情。
   * POST /gobestads/operation/adsEmailSendLog/getDetail
   */
  function getSendLogDetail(id: number): Promise<EmailSendLogDetail> {
    return requestClient
      .post<EmailSendLogDetailResponse>(prefix('adsEmailSendLog/getDetail'), {
        id,
      })
      .then((res) => ({
        batchNo: res.batchNo,
        customerName: res.customerName,
        id: res.id,
        isTest: res.isTest,
        locale: res.locale,
        merchantId: res.customerId,
        messageId: res.messageId,
        operator: res.operator,
        reason: res.reason,
        recipients: res.recipientEmailMasked,
        requestBody: res.requestBody,
        requestId: res.requestId,
        responseBody: res.responseBody,
        retryCount: res.retryCount,
        scene: res.sceneCode,
        sendTime: res.createdTime,
        sentAt: res.sentAt,
        status: res.sendStatus,
        templateCode: res.templateCode,
        triggerRef: res.triggerRef,
        triggerSource: res.triggerSource,
      }))
  }

  /**
   * 导出邮件发送日志。
   * POST /gobestads/operation/export/submit
   */
  function exportSendLogs(query?: EmailSendLogQuery) {
    return exportTaskApi.submitExportTask(
      EXPORT_TASK_BIZ_CODE.ADS_EMAIL_SEND_LOG_EXPORT,
      { condition: getSendLogCondition(query) },
    )
  }

  /**
   * 查询业务整体邮件统计。
   * POST /gobestads/operation/adsEmailUsage/overview
   */
  function getBusinessUsage(
    query?: EmailUsageQuery,
  ): Promise<EmailBusinessUsageReport> {
    return requestClient
      .post<EmailBusinessUsageResponse>(prefix('adsEmailUsage/overview'), {
        condition: getUsageCondition(query),
      })
      .then((res) => {
        const dailyMap = new Map<
          string,
          Partial<Record<EmailTemplateItem['scene'], number>>
        >()

        for (const item of res.trend ?? []) {
          const values = dailyMap.get(item.statDate) ?? {}
          values[item.sceneCode] = item.requestCnt
          dailyMap.set(item.statDate, values)
        }

        return {
          daily: Array.from(dailyMap, ([date, values]) => ({ date, values })),
          sceneItems: (res.sceneList ?? []).map((item) => ({
            acceptedCount: item.acceptedCnt,
            acceptanceRate: item.acceptRate,
            failedCount: item.deadCnt,
            recipientCount: item.recipientCnt,
            requestCount: item.requestCnt,
            scene: item.sceneCode,
            suppressedCount: item.suppressedBatchCnt,
          })),
          summary: {
            acceptedCount: res.summary?.acceptedCnt,
            failedCount: res.summary?.deadCnt,
            recipientCount: res.summary?.recipientCnt,
            requestCount: res.summary?.requestCnt,
            suppressedCount: res.summary?.suppressedBatchCnt,
          },
        }
      })
  }

  /**
   * 导出业务整体邮件统计。
   * POST /gobestads/operation/export/submit
   */
  function exportBusinessUsage(query?: EmailUsageQuery) {
    return exportTaskApi.submitExportTask(
      EXPORT_TASK_BIZ_CODE.ADS_EMAIL_USAGE_OVERVIEW_EXPORT,
      { condition: getUsageCondition(query) },
    )
  }

  /**
   * 查询商户维度邮件统计。
   * POST /gobestads/operation/adsEmailUsage/merchant
   */
  function getMerchantUsage(
    params: EmailMerchantUsagePageRequest,
  ): Promise<EmailMerchantUsageReport> {
    return requestClient
      .post<EmailMerchantUsageResponse>(prefix('adsEmailUsage/merchant'), {
        condition: getMerchantUsageCondition(params.condition),
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
      })
      .then((res) => ({
        items: (res.list ?? []).map((item) => ({
          acceptanceRate: item.acceptRate,
          acceptedCount: item.acceptedCnt,
          customerName: item.customerName,
          failedCount: item.deadCnt,
          merchantId: item.customerId,
          recipientCount: item.recipientCnt,
          requestCount: item.requestCnt,
          suppressedCount: item.suppressedBatchCnt,
          topScene: item.topSceneCode,
        })),
        pageIndex: res.pageIndex,
        pageSize: res.pageSize,
        summary: {
          coveredMerchantCount: res.summary?.merchantCnt,
          recipientCount: res.summary?.recipientCnt,
          requestCount: res.summary?.requestCnt,
          suppressedCount: res.summary?.suppressedBatchCnt,
        },
        topItems: (res.topN ?? []).map((item) => ({
          customerName: item.customerName,
          merchantId: item.customerId,
          recipientCount: item.recipientCnt,
          requestCount: item.requestCnt,
        })),
        total: res.total,
      }))
  }

  /**
   * 导出商户维度邮件统计。
   * POST /gobestads/operation/export/submit
   */
  function exportMerchantUsage(query?: EmailUsageQuery) {
    return exportTaskApi.submitExportTask(
      EXPORT_TASK_BIZ_CODE.ADS_EMAIL_USAGE_MERCHANT_EXPORT,
      { condition: getMerchantUsageCondition(query) },
    )
  }

  return {
    exportBusinessUsage,
    exportMerchantUsage,
    exportSendLogs,
    getBusinessUsage,
    getMerchantUsage,
    getSendLogDetail,
    getTemplate,
    previewTemplate,
    publishTemplate,
    querySendLogs,
    queryTemplates,
    testSendTemplate,
  }
})
